import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LiquidBody, type LiquidEnv, type LiquidReaction } from '@/components/observer/LiquidBody';
import { CALM, PRESETS, pullStrength, type LiquidParams } from '@/components/observer/liquid';
import { STATE_COLOR, STATE_COLOR_MS } from '@/components/observer/companionTheme';
import type { ObserverState } from '@/components/observer/observerContext';
import { Button, IconButton, Switch } from '@/components/ui';
import { useTheme } from '@/theme/ThemeProvider';

/*
  /design/observer/liquid — стенд жидкого тела (docs/observer-design.md §4.7).
  Контур считается по радиальной функции, каждое слагаемое — на своём ползунке:
  дыхание, желе, перемешивание, поток, рябь, шипы, притяжение к курсору, волна от камня. Пресеты состояний
  выставляют ползунки разом. Курсор — магнит: сила растёт с приближением.
  «Готово» и «ошибка» играют реакцию (разлив с пульсом, ёж), «Шаг готов» — тот же разлив с пульсом
  поменьше, после которого возвращается рабочий цвет;
  после «готово» тело само возвращается в покой, как будет в интерфейсе.
*/

/** Сколько «готово» держится, прежде чем тело вернётся в покой: разлив, пульс и пауза. */
const SUCCESS_HOLD_MS = 2600;

const states: { value: ObserverState; label: string }[] = [
  { value: 'idle', label: 'покой' },
  { value: 'thinking', label: 'думает' },
  { value: 'writing', label: 'пишет' },
  { value: 'download', label: 'качает' },
  { value: 'upload', label: 'отдаёт' },
  { value: 'update', label: 'долгая' },
  { value: 'success', label: 'готово' },
  { value: 'error', label: 'ошибка' },
];

function Range({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  return (
    <label className="flex items-center gap-3 text-xs">
      <span className="w-28 shrink-0 text-fg-muted">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 flex-1 cursor-pointer accent-brand"
      />
      <span className="w-12 shrink-0 text-right text-fg-subtle tabular-nums">
        {format ? format(value) : value}
      </span>
    </label>
  );
}

/**
 * Притяжение относительно ПОВЕРХНОСТИ тела, а не центра: сила максимальна у края и спадает
 * с расстоянием от края; внутри тела она гаснет к центру, чтобы курсор над телом не дёргал
 * выступ по кругу. reach = 0 выключает магнит. bodyRadius — радиус тела в px.
 */
function envAround(
  el: HTMLElement | null,
  cursor: { x: number; y: number },
  reach: number,
  bodyRadius: number,
): LiquidEnv {
  if (!el) return { pull: { angle: 0, strength: 0 } };
  const r = el.getBoundingClientRect();
  const dx = cursor.x - (r.left + r.width / 2);
  const dy = cursor.y - (r.top + r.height / 2);
  const d = Math.hypot(dx, dy);
  const strength =
    d >= bodyRadius
      ? pullStrength(d - bodyRadius, reach)
      : pullStrength(0, reach) * Math.pow(d / bodyRadius, 2);
  return { pull: { angle: Math.atan2(dy, dx), strength } };
}

export function LiquidLab() {
  const { mode, toggle } = useTheme();
  const [state, setState] = useState<ObserverState>('idle');
  const [params, setParams] = useState<LiquidParams>(CALM);
  const [magnet, setMagnet] = useState(true);
  const [reach, setReach] = useState(140);
  const [splashAt, setSplashAt] = useState<number | null>(null);
  const [waveCount, setWaveCount] = useState(3);
  const [reaction, setReaction] = useState<LiquidReaction | null>(null);
  const [holdSpikes, setHoldSpikes] = useState(false);
  const bigRef = useRef<HTMLDivElement>(null);
  const smallRef = useRef<HTMLDivElement>(null);
  const cursor = useRef({ x: -9999, y: -9999 });
  const backToIdle = useRef<number | null>(null);

  const applyPreset = useCallback((s: ObserverState) => {
    if (backToIdle.current !== null) window.clearTimeout(backToIdle.current);
    backToIdle.current = null;
    setState(s);
    setParams({ ...CALM, ...(PRESETS[s] ?? {}) });
    setReaction(s === 'success' || s === 'error' ? { kind: s, at: performance.now() } : null);
    if (s === 'success') {
      backToIdle.current = window.setTimeout(() => {
        backToIdle.current = null;
        setState('idle');
        setReaction(null);
      }, SUCCESS_HOLD_MS);
    }
  }, []);

  useEffect(
    () => () => {
      if (backToIdle.current !== null) window.clearTimeout(backToIdle.current);
    },
    [],
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      cursor.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  /** Среда для тела: притяжение по закону расстояния и взгляд на курсор. Считается в кадре. */
  const bigEnv = useCallback(
    () => envAround(bigRef.current, cursor.current, magnet ? reach : 0, 100),
    [magnet, reach],
  );
  const smallEnv = useCallback(
    () => envAround(smallRef.current, cursor.current, magnet ? reach / 3 : 0, 16),
    [magnet, reach],
  );

  type GroupKey = Exclude<keyof LiquidParams, 'radius'>;
  const set = <K extends GroupKey>(key: K, patch: Partial<LiquidParams[K]>) =>
    setParams((p) => ({ ...p, [key]: Object.assign({}, p[key], patch) }));

  const color = STATE_COLOR[state];
  const colorMs = STATE_COLOR_MS[state];
  const auraPulse = state === 'thinking' || state === 'update';
  const auraDim = state === 'error';

  return (
    <div className="h-screen overflow-y-auto bg-frame text-fg">
      <header className="sticky top-0 z-raised flex items-center gap-4 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design/observer/lab"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Стенд
        </Link>
        <span className="text-sm font-semibold text-fg">Жидкое тело</span>
        <div className="ml-auto">
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[1fr_380px]">
        <section className="rounded-lg border border-line bg-card p-6">
          <div className="grid gap-6 md:grid-cols-[1fr_1fr]">
            <div className="flex flex-col items-center gap-4 rounded-md border border-line bg-canvas p-6">
              <div className="text-2xs font-semibold text-fg-subtle uppercase">Крупно · подведите курсор</div>
              <div ref={bigRef} className="grid h-72 w-full place-items-center">
                <LiquidBody
                  params={params}
                  size={200}
                  color={color}
                  colorMs={colorMs}
                  splashAt={splashAt}
                  getEnv={bigEnv}
                  waveCount={waveCount}
                  auraPulse={auraPulse}
                  reaction={reaction}
                  holdSpikes={holdSpikes}
                  auraDim={auraDim}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" variant="secondary" onClick={() => setSplashAt(performance.now())}>
                  Бросить камень
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setReaction({ kind: 'step', at: performance.now() })}
                >
                  Шаг готов
                </Button>
                <Switch size="sm" checked={magnet} onCheckedChange={setMagnet} label="Курсор как магнит" />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="text-2xs font-semibold text-fg-subtle uppercase">Настоящий размер</div>
              <div className="relative mt-4 rounded-md border border-line bg-canvas p-5">
                <div ref={smallRef} className="absolute top-0 right-8 -translate-y-1/2">
                  <LiquidBody
                    params={params}
                    size={32}
                    color={color}
                    colorMs={colorMs}
                    splashAt={splashAt}
                    getEnv={smallEnv}
                    waveCount={waveCount}
                    auraPulse={auraPulse}
                    reaction={reaction}
                    holdSpikes={holdSpikes}
                    auraDim={auraDim}
                  />
                </div>
                <div className="text-sm font-semibold text-fg">Карточка</div>
                <p className="mt-1 text-xs text-fg-muted">
                  Он же на верхней границе блока. Здесь проверяем, что читается в тридцати двух пикселях, а
                  что живёт только крупно.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {['Строка списка', 'Ещё строка', 'И ещё одна'].map((t) => (
                    <div key={t} className="rounded-sm border border-line px-3 py-2 text-xs text-fg-muted">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className="flex flex-col gap-5">
          <section className="rounded-lg border border-line bg-card p-5">
            <h2 className="type-subheading text-fg">Пресеты состояний</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {states.map((s) => (
                <Button
                  key={s.value}
                  size="sm"
                  variant={state === s.value ? 'primary' : 'secondary'}
                  onClick={() => applyPreset(s.value)}
                >
                  {s.label}
                </Button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-line bg-card p-5">
            <h2 className="type-subheading text-fg">Слагаемые формы</h2>
            <p className="mt-1 text-xs text-fg-muted">
              Каждый ползунок — одно поведение. Пресет выше выставляет их разом, дальше крутите руками.
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <div className="text-2xs font-semibold text-fg-subtle uppercase">Дыхание</div>
              <Range
                label="амплитуда"
                value={params.breathe.amp}
                min={0}
                max={0.12}
                step={0.005}
                onChange={(v) => set('breathe', { amp: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="скорость"
                value={params.breathe.speed}
                min={0.2}
                max={5}
                step={0.1}
                onChange={(v) => set('breathe', { speed: v })}
              />

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Желе</div>
              <Range
                label="амплитуда"
                value={params.jelly.amp}
                min={0}
                max={0.16}
                step={0.005}
                onChange={(v) => set('jelly', { amp: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="скорость"
                value={params.jelly.speed}
                min={0.2}
                max={4}
                step={0.1}
                onChange={(v) => set('jelly', { speed: v })}
              />

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Перемешивание</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-28 shrink-0 text-fg-muted">направление</span>
                {([-1, 0, 1] as const).map((d) => (
                  <Button
                    key={d}
                    size="sm"
                    variant={params.stir.dir === d ? 'primary' : 'secondary'}
                    onClick={() => set('stir', { dir: d })}
                  >
                    {d === -1 ? 'против' : d === 0 ? 'нет' : 'по часовой'}
                  </Button>
                ))}
              </div>
              <Range
                label="сила вихря"
                value={params.stir.strength}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => set('stir', { strength: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="скорость"
                value={params.stir.speed}
                min={0.2}
                max={3}
                step={0.1}
                onChange={(v) => set('stir', { speed: v })}
              />
              <Range
                label="закрутка"
                value={params.stir.twist}
                min={0.2}
                max={1.6}
                step={0.05}
                onChange={(v) => set('stir', { twist: v })}
                format={(v) => `${v.toFixed(2)} об.`}
              />

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Поток по поверхности</div>
              <Range
                label="сила"
                value={params.flow.strength}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => set('flow', { strength: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="скорость"
                value={params.flow.speed}
                min={0.2}
                max={3}
                step={0.1}
                onChange={(v) => set('flow', { speed: v })}
              />
              <Range
                label="толщина"
                value={params.flow.width}
                min={2}
                max={14}
                step={0.5}
                onChange={(v) => set('flow', { width: v })}
              />
              <Range
                label="волна по краю"
                value={params.flow.edge}
                min={0}
                max={0.2}
                step={0.01}
                onChange={(v) => set('flow', { edge: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <div className="flex items-center gap-2 text-xs">
                <span className="w-28 shrink-0 text-fg-muted">направление</span>
                {([-1, 0, 1] as const).map((d) => (
                  <Button
                    key={d}
                    size="sm"
                    variant={params.flow.dir === d ? 'primary' : 'secondary'}
                    onClick={() => set('flow', { dir: d })}
                  >
                    {d === -1 ? 'вверх' : d === 0 ? 'нет' : 'вниз'}
                  </Button>
                ))}
              </div>

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Рябь контура</div>
              <Range
                label="амплитуда"
                value={params.ripple.amp}
                min={0}
                max={0.14}
                step={0.005}
                onChange={(v) => set('ripple', { amp: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="скорость"
                value={params.ripple.speed}
                min={0.5}
                max={8}
                step={0.1}
                onChange={(v) => set('ripple', { speed: v })}
              />
              <div className="flex items-center gap-2 text-xs">
                <span className="w-28 shrink-0 text-fg-muted">направление</span>
                {([-1, 0, 1] as const).map((d) => (
                  <Button
                    key={d}
                    size="sm"
                    variant={params.ripple.dir === d ? 'primary' : 'secondary'}
                    onClick={() => set('ripple', { dir: d })}
                  >
                    {d === -1 ? 'вверх' : d === 0 ? 'нет' : 'вниз'}
                  </Button>
                ))}
              </div>

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Шипы · ёж при ошибке</div>
              <Switch
                size="sm"
                checked={holdSpikes}
                onCheckedChange={setHoldSpikes}
                label="Держать шипы, чтобы подобрать вид"
              />
              <Range
                label="сколько"
                value={params.spikes.count}
                min={4}
                max={24}
                step={1}
                onChange={(v) => set('spikes', { count: v })}
              />
              <Range
                label="высота"
                value={params.spikes.amp}
                min={0}
                max={0.45}
                step={0.01}
                onChange={(v) => set('spikes', { amp: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="острота"
                value={params.spikes.sharp}
                min={1}
                max={24}
                step={1}
                onChange={(v) => set('spikes', { sharp: v })}
              />
              <Range
                label="дрожь"
                value={params.spikes.tremble}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => set('spikes', { tremble: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
              <Range
                label="опадание, с"
                value={params.spikes.fall}
                min={0.3}
                max={3}
                step={0.1}
                onChange={(v) => set('spikes', { fall: v })}
                format={(v) => v.toFixed(1)}
              />

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Магнит</div>
              <Range
                label="радиус от края, px"
                value={reach}
                min={20}
                max={400}
                step={10}
                onChange={setReach}
              />

              <div className="mt-2 text-2xs font-semibold text-fg-subtle uppercase">Камень</div>
              <Range
                label="волн от удара"
                value={waveCount}
                min={1}
                max={4}
                step={1}
                onChange={setWaveCount}
              />
              <Range
                label="сила удара"
                value={params.splash.amp}
                min={0}
                max={0.3}
                step={0.01}
                onChange={(v) => set('splash', { amp: v })}
                format={(v) => `${Math.round(v * 100)} %`}
              />
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
}
